import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import UploadFileInput from '@repo/ui/file-input'
import { DownloadFile } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import { SendEmailInput } from '../../../lib/types/models/mail/create-mail-attachment'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { RICH_TEXT_SCHEMA } from '../../../lib/utils/validations/rich-text.validation'
import {
  createStringArraySchema,
  createStringSchema
} from '../../../lib/utils/validations/string-validation'
import { t } from '../../../locale/utils'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'
import ValidationButtons from '../../elements/validation-buttons'

interface Props extends BaseFormProps<SendEmailInput> {
  className?: string
  labels: string[]
  customComponent?: React.ReactNode
}

const formSchema = (language: AvailableLanguages): ZodType<NullableTransformer<SendEmailInput>> =>
  z.object({
    emails: createStringArraySchema('emails'),
    subject: createStringSchema('a subject', { language }),
    body: RICH_TEXT_SCHEMA(language),
    attachments: z.array(FILE_VALIDATION()).nullish(),
    cc: createStringArraySchema('cc').nullish(),
    bcc: createStringArraySchema('bcc').nullish()
  })

export default function SendEmailForm({
  className,
  defaultValues,
  labels,
  customComponent,
  ...validationButtonProps
}: Props) {
  const { currentLanguage } = useTranslate()
  const form = useForm<SendEmailInput>({
    resolver: zodResolver(formSchema(currentLanguage)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex gap-4 flex-col', className)}>
          <div className="flex gap-2 items-center">
            <Typography variant="label-medium">{t('send.email.to')}</Typography>
            <div className="flex gap-2 flex-wrap flex-1 items-center">
              {labels?.map((label, index) => (
                <Badge key={`${label}-${index}`} text={label} size={'small'} variant={'grey'} />
              ))}
            </div>
          </div>

          <FormField
            control={form.control}
            name={'cc'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    type="email"
                    label="CC"
                    variant="primary"
                    onChange={field.onChange}
                    selectedOptions={field.value?.map((cc) => ({ label: cc, value: cc })) ?? []}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'subject'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    required
                    {...field}
                    type={'text'}
                    label={t('send.email.subject')}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'body'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledTextEditor required {...field} placeholder={t('send.email.body')} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'attachments'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <UploadFileInput
                    placeholder={t('send.email.attachments.upload')}
                    types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF, FileTypeEnum.VIDEO]}
                    icon={DownloadFile}
                    previewOnClick={true}
                    label={t('send.email.attachments')}
                    multiple
                    files={field?.value}
                    onFilesChange={(files) => field.onChange(files)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {customComponent}
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
